<template >
        <div v-if="isLoading" class="flex justify-center items-center h-screen">
        <div class="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        </div>
        <div class="bg-blue-50">
            <div v-if="!isLoading" class="header-footer">
            <HeaderComponent />
            </div>
            <div v-if="!isLoading && showErrorMessage" style="font-size: 1.85rem;">
            <div class="flex flex-col justify-center items-center" style="height: 64.5vh;">
                <!--<i class="fa-duotone fa-solid fa-triangle-exclamation text-5xl text-red-700"></i>-->
                <img src="@/assets/error_icon.svg" alt="signature" width="90px;" height="55px;">
                <span>{{ errorMessage }}</span> 
            </div>
            </div>
            <div v-if="!hasSurveyId" class="m-10">
              <div class="flex flex-col md:flex-row gap-10">
                <div class="md:w-1/2 bg-blue-50 rounded-lg order-2 md:order-1 p-6">
                  <h1 class="text-3xl font-bold mb-4 text-blue-800 text-center">Transform Your Data Collection with Salesforce-Powered Forms</h1><br>
                  <p class="mb-4">
                    Experience a modern, dynamic, and user-friendly approach to collecting data that seamlessly integrates with Salesforce. Our demo showcases a highly interactive form designed to enhance user experience while efficiently managing responses.
                  </p>

                  <h3 class="text-xl font-semibold mb-3 text-blue-700">What to Expect in This Demo:</h3>
                  <ol class="list-decimal pl-5 mb-4 space-y-2">
                    <li>
                      <strong>Interactive Question Flow</strong>
                      <ul class="list-disc pl-5 mb-4 space-y-2">
                        <li>Questions appear <strong>one at a time</strong> to keep users focused and engaged.</li>
                        <li> Supports multiple input types including:</li>
                        <ul class="list-circle-custom pl-5 mb-4 space-y-2">
                          <li><strong>Text Fields</strong>: Collect open-ended responses.</li>
                          <li><strong>Single-Choice Options</strong>: Simplify decision-making with radio buttons.</li>
                          <li><strong>Multiple-Choice Options</strong>: Allow flexibility with checkboxes.</li>
                          <li><strong>Rating Scales</strong>: Gather precise feedback through intuitive rating tools.</li>
                        </ul>
                      </ul>
                    </li>
                    <li>
                      <strong>Real-Time Experience</strong> 
                      <ul class="list-disc pl-5 mb-4 space-y-2">
                        <li>The demo provides a <strong>live, client-side preview</strong> of the form experience.</li>
                        <li>Navigate through an intuitive, clean design that prioritizes usability.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Salesforce Integration</strong> 
                      <ul class="list-disc pl-5 mb-4 space-y-2">
                        <li>Upon submission, all form data is automatically <strong>stored in Salesforce</strong>.</li>
                        <li>Perfect for lead generation, surveys, feedback collection, or customer onboarding.</li>
                        <li>Experience how Salesforce becomes the central hub for your data.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Customization & Branding</strong> 
                      <ul class="list-disc pl-5 mb-4 space-y-2">
                        <li>Every form element can be tailored to reflect your <strong>brand identity</strong>.</li>
                        <li> Customizable options include:</li>
                        <ul class="list-circle-custom pl-5 mb-4 space-y-2">
                          <li><strong>Colors</strong> and <strong>fonts</strong> matching your brand guidelines.</li>
                          <li>Personalized <strong>logos</strong>, <strong>headers</strong> and <strong>layouts</strong>.</li>
                          <li>Question flow and field types suited to your specific use case.</li>
                        </ul>
                      </ul>
                    </li>
                  </ol>

                  <h3 class="text-xl font-semibold mb-3 text-blue-700">Why Choose Our Forms Solution?</h3>
                  <ul class="list-disc pl-5 mb-4 space-y-2">
                    <li><strong>Improved User Engagement</strong>: The step-by-step form format makes it simple and less overwhelming for users to provide information.</li>
                    <li><strong>Seamless Data Management</strong>: By connecting directly to Salesforce, you eliminate manual data entry and ensure information is accessible in real time.</li>
                    <li><strong>Scalable and Flexible</strong>: From small surveys to detailed lead generation forms, our solution scales with your business requirements.</li>
                    <li><strong>Enhanced Brand Experience</strong>: Forms can be completely customized to match your company’s branding and style, delivering a professional impression every time.</li>
                  </ul>

                  <h3 class="text-xl font-semibold mb-3 text-blue-700">Perfect for Various Use Cases:</h3>
                  <ul class="list-disc pl-5 mb-4 space-y-2">
                    <li><strong>Lead Capture Forms</strong>: Gather and store customer data effortlessly.</li>
                    <li><strong>Customer Feedback Surveys</strong>: Collect ratings and reviews to improve your services.</li>
                    <li><strong>Event Registrations</strong>: Simplify sign-ups for webinars or workshops.</li>
                    <li><strong>Employee Onboarding</strong>: Streamline internal processes with step-based forms.</li>
                  </ul>

                  <h3 class="text-xl font-semibold mb-3 text-blue-700">Get Started Now</h3>
                  <p class="mb-4">
                    🚀 <strong>Start the Demo</strong> to experience how our Salesforce form solution can make data collection efficient, user-friendly, and aligned with your goals.
                  </p>

                  <h4 class="font-bold text-blue-800 text-center">Let’s redefine the way you collect and manage data!</h4>

                </div>
                <div class="md:w-1/2 order-1 md:order-2 p-6">
                  <!--<DemoForm leadType="survey"/>-->
                  <DemoForm  leadType="survey" :pageTitle="pageTitle" :pageDescription:="pageDescription" :currentUrl:="currentUrl"/>
                </div>
              </div>
            </div>
            <div v-if="!isLoading && !showErrorMessage && hasSurveyId" class="page">
                <header class="vff-header" style="padding-top: 10px;">
                    <div class="f-container">
                    <button @click="getData" > Show JSON</button>
                    </div>
                </header>
        
                <client-only>
                    <flow-form 
                    v-bind:questions="questions" 
                    v-bind:language="language"
                    v-on:complete="onComplete"
                    v-on:submit="onSubmit"
                    />
                </client-only>
            </div>
            <div v-if="!isLoading" class="header-footer">
            <FooterComponent />
            </div>
        </div>
  </template>
  
  <script>
  import {FlowForm, QuestionModel, QuestionType, ChoiceOption, LanguageModel, /*MatrixColumn, MatrixRow*/} from '@ditdot-dev/vue-flow-form'  
  import { getSurveyData, postSurveyData } from '@/api/wordpress';
  import DemoForm from '../components/DemoForm.vue';
  import { useRoute, useRouter } from 'vue-router';
  import {useCommonHead} from '../composables/useCommonHead';
  import HeaderComponent from "../components/Header.vue";
  import FooterComponent from "../components/Footer.vue";
  import '@ditdot-dev/vue-flow-form/dist/vue-flow-form.css';
  import '@ditdot-dev/vue-flow-form/dist/vue-flow-form.theme-purple.css';
  export default {
  name: 'example-1',
  components: {
    FlowForm, DemoForm, HeaderComponent, FooterComponent
  },
  data() {
  return {
    language: new LanguageModel(),
    isLoading: true,
    hasSurveyId:false,
    surveyData:{},
    pageTitle: 'Survey Demo',
    pageDescription: 'Participate in our survey to share your feedback!',
    currentUrl: '',
    errorMessage:'',
    showErrorMessage:false,
    questions: [
      new QuestionModel({
        title: 'Email',
        type: QuestionType.Email,
        required: true,
        placeholder: 'Enter your email',
        question: "Imagine you're joining our VIP list! What's your email to stay in the loop?"
      }),
      new QuestionModel({
        title: 'Ideal Customer Service',
        type: QuestionType.Text,
        required: true,
        placeholder: 'Enter one word',
        question: "If you could describe your ideal customer service experience in one word, what would it be?"
      }),
      new QuestionModel({
        tagline: 'Magic Solution',
        title: 'If we could offer you a magic solution to one problem, which would it be?',
        subtitle: '',
        type: QuestionType.MultipleChoice,
        multiple: false,
        required: true,
        options: [
          new ChoiceOption({
            label: 'Time Management',
            value: 'time_management'
          }),
          new ChoiceOption({
            label: 'Business Growth',
            value: 'business_growth'
          }),
          new ChoiceOption({
            label: 'Tech Integration',
            value: 'tech_integration'
          }),
          new ChoiceOption({
            label: 'Data Security',
            value: 'data_security'
          })
        ]
      }),
      new QuestionModel({
        tagline: 'Tech Partner Qualities',
        title: 'What qualities do you value most in a tech partner? (Select all that apply)',
        type: QuestionType.MultipleChoice,
        multiple: false,
        required: true,
        options: [
          new ChoiceOption({
            label: 'Innovation',
            value: 'innovation'
          }),
          new ChoiceOption({
            label: 'Responsiveness',
            value: 'responsiveness'
          }),
          new ChoiceOption({
            label: 'Cost-Effectiveness',
            value: 'cost_effectiveness'
          }),
          new ChoiceOption({
            label: 'Customization',
            value: 'customization'
          }),
          new ChoiceOption({
            label: 'Trustworthiness',
            value: 'trustworthiness'
          })
        ]
      }),
      new QuestionModel({
        tagline: 'Website Ease of Use',
        title: "Rate our website's ease of use, on a scale of 1 to 5 stars. 🌟",
        type: QuestionType.OpinionScale,
        required: true,
        min: 1,
        max: 5,
        labelLeft: '1 star',
        labelRight: '5 stars'
      }),
      new QuestionModel({
        tagline: 'Excitement for Cutting-edge Technology',
        title: "On a scale of 1 to 5, how excited are you about using cutting-edge technology like Salesforce to transform your business?",
        type: QuestionType.Number,
        required: true,
        min: 1,
        max: 5,
      })
    ]
  }
  },
  
  async created() {
    await this.fetchSurveyData();
  },
  
  watch: {
    '$route.query.id'(newId) {
      if (newId) {
        this.fetchSurveyData();
      }
    }
  },
  
  mounted() {
    // Initial call to set the head
    useCommonHead(this.pageTitle, this.pageDescription, this.currentUrl);
  },
  
  /*setup() {
    //Set up head using useCommonHead
    const pageTitle = 'Survey Demo';
    const pageDescription = 'Participate in our survey to share your feedback!';
    const currentUrl = '';
    useCommonHead(this.pageTitle, this.pageDescription, this.currentUrl);    
  },*/
  
  methods: {
    async fetchSurveyData() {
      try {
        const recordId = this.$route.query.id;
      
        console.log('recordId -> : ' + recordId);
        if (recordId) {
          this.hasSurveyId = true;
          console.log('hasSurveyId -> : ' + this.hasSurveyId);
  
          const endpointUrl = `/demo/survey?id=${recordId}`;
          const response = await getSurveyData(endpointUrl);
          console.log(response);
          if (response.success) {
            setTimeout(() => {
              this.isLoading = false;
            }, 2000);
  
            this.surveyData = response.data;
            console.log('surveyData');
            console.log(this.surveyData);
  
            if(this.surveyData.surveyWrapper.isSurveyCompleted){
              this.showErrorMessage = true;
              this.errorMessage = 'Survey already Completed.';
            }else{
              this.showErrorMessage = false;
            }
          } else { 
              console.log('response.success : ' + response.success);
          }
        } else {
          this.isLoading = false;
        }
        
      } catch (error) {
        this.isLoading = false;
        this.showErrorMessage = true;
        this.errorMessage = 'The URL you provided is invalid or has expired.';
        console.error('Failed to fetch survey Data:', error);
      } 
    },
  
    onComplete(completed, questionList) {
      console.log('Quiz completed:', completed)
      console.log('Answers:', questionList)
    },
  
    onSubmit(questionList) {
      this.sendData()
    },
  
    async sendData() {
      this.isLoading = true;
  
      const data = this.getData();
      console.log('data to be sent:');
      console.log(data);
  
      this.surveyData.surveyWrapper.surveyJson = JSON.stringify(data);
      console.log('surveyData');
      console.log(this.surveyData);
  
      const endpointUrl = `/demo/survey`;
      console.log('endpointUrl : ' + endpointUrl);
  
      try {
        const response = await postSurveyData(endpointUrl, this.surveyData);
        if (response.success) {
          console.log(response);
          setTimeout(() => {
            this.isLoading = false;
          }, 2000);
          console.log('Survey Form submitted!');
          console.log(response.data);
          this.$router.push({path: '/demo/survey-success', query: {id: this.surveyData.surveyWrapper.surveyId}});
        }else { 
          console.log('response.success : ' + response.success);
        }
      } catch (error) {
        this.isLoading = false;
        console.error('Error submitting Survey form:');
        console.error(error);
      } 
    },
  
    getData() {
      const data = {
        questions: [],
        answers: []
      }
      console.log(this.questions);
  
      this.questions.forEach(question => {
        if (question.title) {
          let answer = question.answer
          if (Array.isArray(answer)) {
            answer = answer.join(', ')
          }
          data.questions.push(question.title)
          data.answers.push(answer)
        }
      })
      return data
    }
  }
  }
  </script>
  
  <style>

.list-circle-custom {
  list-style-type: circle;  /* Hollow circle */
}

  
  .page {
    min-height: 20cm;
    border: 1px solid #d3d3d3;
    box-shadow: 0 0 5px rgba(143, 58, 58, 0.1);
    position: relative;
    font-family: Arial, sans-serif;
    font-size:16px;
  }
  
  .vff-footer {
      font-weight: 300;
      position: relative;
  }
  
  .vff-footer .footer-inner-wrap {
    background-color: transparent; 
  }
  
  .header-footer{
    width: 100%;
  }
  /* Import Vue Flow Form base CSS */
  /* Import one of the Vue Flow Form CSS themes (optional) */
  /*@import '~@ditdot-dev/vue-flow-form/dist/vue-flow-form.theme-minimal.css';*/
  /* @import '~@ditdot-dev/vue-flow-form/dist/vue-flow-form.theme-green.css'; */
  </style>